import React, { useEffect, useState } from "react";
import { api, euro, formatApiErrorDetail } from "@/lib/api";
import { PhoneDisplay } from "@/components/PhoneDisplay";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { ServiceHistoryButton } from "@/components/ServiceHistoryButton";
import { SimammaBadge } from "@/components/Logo";
import { HOUSE_OPERATOR } from "@/lib/brand";
import { toast } from "sonner";
import { BadgePercent } from "lucide-react";

export default function Promotions() {
  const [promos, setPromos] = useState([]);
  const [sims, setSims] = useState([]);
  const [simId, setSimId] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/simamma/promotions").then((r) => setPromos(r.data.promotions || [])).catch(() => {});
    api.get("/sims").then((r) => setSims((r.data || []).filter((s) => s.operator === HOUSE_OPERATOR))).catch(() => {});
  }, []);

  const activate = async () => {
    if (!simId) { toast.error("Seleziona una SIM SiMamma"); return; }
    if (!selected) { toast.error("Seleziona un'offerta"); return; }
    setLoading(true);
    try {
      await api.post("/simamma/activate-promo", { sim_id: simId, promo_code: selected });
      toast.success("Offerta attivata sulla SIM");
      setSelected(null);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore attivazione");
    } finally { setLoading(false); }
  };

  return (
    <div className="space-y-6 max-w-3xl" data-testid="promotions-page">
      <ServiceHistoryButton service="promozioni" />
      <div>
        <div className="flex items-center gap-2"><SimammaBadge /><span className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Servizio operatore</span></div>
        <h1 className="font-head text-3xl font-bold tracking-tight mt-2" data-testid="simamma-promotions-title">Promozioni SiMamma</h1>
      </div>

      <Card className="p-5 glow-border">
        <Label>SIM SiMamma del cliente</Label>
        <Select value={simId} onValueChange={setSimId}>
          <SelectTrigger className="mt-1.5" data-testid="promo-sim-select"><SelectValue placeholder="Seleziona SIM attiva" /></SelectTrigger>
          <SelectContent>{sims.map((s) => <SelectItem key={s.id} value={s.id}><PhoneDisplay value={s.phone_number} data-testid={`promotion-sim-phone-${s.id}`} /> — {s.customer_name}</SelectItem>)}</SelectContent>
        </Select>
        {sims.length === 0 && <p className="text-xs text-amber-500 mt-1" data-testid="simamma-promotions-empty">Nessuna SIM SiMamma attiva disponibile per questo account.</p>}
      </Card>

      <div className="grid sm:grid-cols-3 gap-4">
        {promos.map((p) => (
          <button key={p.code} type="button" data-testid={`promo-card-${p.code}`} onClick={() => setSelected(p.code)}
            className={`text-left rounded-xl border p-5 transition-all ${selected === p.code ? "border-primary bg-primary/10 ring-2 ring-primary" : "border-border hover:border-primary/50"}`}>
            <Badge variant="outline" className="text-xs">{p.data_gb}</Badge>
            <h3 className="font-head font-semibold text-lg mt-2">{p.name}</h3>
            <p className="text-sm text-muted-foreground mt-1">{p.description}</p>
            <p className="font-head text-2xl font-bold mt-3">{euro(p.price)}<span className="text-sm font-normal text-muted-foreground">/mese</span></p>
          </button>
        ))}
      </div>

      <Button onClick={activate} disabled={loading} className="gap-2" data-testid="promo-activate-button">
        <BadgePercent className="w-4 h-4" /> {loading ? "Attivazione…" : "Attiva offerta"}
      </Button>
    </div>
  );
}
